<template>
    <section class="wrap wrap-large py-12">
        <form
            class="pb-8 at1024:pb-16 px-8 at1024:px-16 border w-full mx-auto border-slate-300 at1024:my-8"
            @invalid="invalidate"
            @submit="submit"
            :class="{ errors: errors }"
        >
            <header class="text-center pt-8">
                <h1 class="text-[40px] at720:text-4xl my-[30px] at1024:text-[80px]">
                    {{ $t("Registration") }}
                </h1>
            </header>

            <div class="text-center">
                <p class="font-medium text-[28px] at1024:text-[35px] mb-[1.1em]">
                    {{ $t("Company Information") }}
                </p>
            </div>

            <fieldset class="flex flex-col w-full gap-3">
                <SeclockInput
                    type="select"
                    :animated="true"
                    :contrast="true"
                    @invalid="invalidate"
                    @change="validate"
                    label="How did you hear about us?"
                    v-model="form.how_hear_about_us"
                    :error="form.how_hear_about_us"
                    required
                >
                    <option
                        v-for="howHearOption in config.howHearOptions"
                        :value="howHearOption"
                    >
                        {{ $t(howHearOption) }}
                    </option>
                </SeclockInput>

                <div v-if="form.how_hear_about_us === ''"></div>

                <div
                    v-else-if="form.how_hear_about_us === 'I am an existing customer'"
                    class="p-4 at768:p-6 bg-[#fff5dd]"
                >
                    If your company is an existing customer with an account,
                    please fill out the
                    <button
                        @click="goToWebAccessForm"
                        type="button"
                        class="text-red hover:text-black hover:underline underline-offset-4"
                    >
                        web access form
                    </button>
                    .
                </div>

                <div v-else class="flex flex-col gap-3">
                    <SeclockInput
                        type="text"
                        v-if="form.how_hear_about_us === 'Referred' || form.how_hear_about_us === 'Other'"
                        :animated="true"
                        :contrast="true"
                        :label="$t('Please Specify Referral Name')"
                        v-model="form.referred_by"
                        maxlength="255"
                        :error="form.referred_by"
                        @invalid="invalidate"
                        required
                    />
                    <SeclockInput
                        type="text"
                        :animated="true"
                        :contrast="true"
                        label="Your Company Name"
                        @invalid="invalidate"
                        :error="form.company_name"
                        v-model="form.company_name"
                        maxlength="35"
                        minlength="1"
                        required
                    />
                    <div class="flex flex-col at768:flex-row gap-3">
                        <div class="at768:w-1/2">
                            <SeclockInput
                                type="tel"
                                :animated="true"
                                :contrast="true"
                                label="Phone"
                                v-model="form.phone"
                                :error="form.phone"
                                @invalid="invalidate"
                                pattern="(1)?(-|.|\s)?[0-9]{3}(-|.|\s)?[0-9]{3}(-|.|\s)?[0-9]{4}"
                                required
                            />
                        </div>
                        <div class="at768:w-1/4">
                            <SeclockInput
                                type="tel"
                                :contrast="true"
                                :animated="true"
                                label="Extension"
                                v-model="form.extension"
                                :error="form.extension"
                                @invalid="invalidate"
                                pattern="[0-9]{0,4}"
                            />
                        </div>
                        <div class="at768:w-1/4">
                            <SeclockInput
                                type="select"
                                :contrast="true"
                                :animated="true"
                                label="Phone Type"
                                @invalid="invalidate"
                                :error="form.phone_type"
                                v-model="form.phone_type"
                                required
                            >
                                <option
                                    v-for="phoneType in config.phoneTypes"
                                    :value="phoneType"
                                >
                                    {{ $t(phoneType) }}
                                </option>
                            </SeclockInput>
                        </div>
                    </div>
                    <SeclockInput
                        type="text"
                        :animated="true"
                        :contrast="true"
                        label="Company Website"
                        v-model="form.company_website"
                        maxlength="200"
                        @invalid="invalidate"
                        :error="form.company_website"
                    />
                    <SeclockInput
                        type="select"
                        :contrast="true"
                        :animated="true"
                        label="Type of Business"
                        v-model="form.business_type"
                        @invalid="invalidate"
                        :error="form.business_type"
                        required
                    >
                        <option
                            v-for="businessType in config.businessTypes"
                            :value="businessType"
                        >
                            {{ $t(businessType) }}
                        </option>
                    </SeclockInput>
                </div>
            </fieldset>

            <div class="flex justify-between items-center mt-4 mb-8">
                <button
                    @click="goBack"
                    type="button"
                    class="uppercase tracking-widest flex gap-1 items-center"
                >
                    <BaseSvg name="icon-chevron-left" />
                    {{ $t("PREV") }}
                </button>
                <button
                    type="submit"
                    :disabled="!isFormValid"
                    class="btn-white-on-black"
                >
                    {{ $t('Next') }}
                    <BaseSvg name="icon-chevron-right" />
                </button>
            </div>
            <RecaptchaDisclaimer class="mt-16 text-center text-gray text-sm" />
        </form>
    </section>
</template>

<script setup>
import { BaseSvg, SeclockInput, RecaptchaDisclaimer } from "@/Components";
import config from "../../config";
import {computed} from "vue";
import {eventBus} from "@/Composables";

const props = defineProps(["form", "errors"]);
const emit = defineEmits(["goToNextStep", "invalidate", "validate"]);

const invalidate = () => {
    emit("invalidate");
};

const validate = () => {
    emit("validate");
};

// thanks Chat-GPT!
const isFormValid = computed(() => {
    // Validate 'How did you hear about us?'
    if (!props.form.how_hear_about_us || props.form.how_hear_about_us === 'I am an existing customer') return false;

    // Validate 'Referred By' or 'Other' specifics if those options are selected
    if ((props.form.how_hear_about_us === 'Referred' || props.form.how_hear_about_us === 'Other') && !props.form.referred_by) {
        return false;
    }

    // Basic text input validation for company name
    if (!props.form.company_name) return false;

    // Phone number validation (must match pattern if provided)
    const phonePattern = /(1)?(-|.|\s)?[0-9]{3}(-|.|\s)?[0-9]{3}(-|.|\s)?[0-9]{4}/;
    if (!props.form.phone || !phonePattern.test(props.form.phone)) return false;

    // Optional extension validation
    const extensionPattern = /[0-9]{0,4}/;
    if (props.form.extension && !extensionPattern.test(props.form.extension)) return false;

    // Phone type selection must be valid if visible
    if (!props.form.phone_type) return false;

    // Company website URL validation (optional unless specific logic is required)
    // const urlPattern = /^(https?:\/\/)?([\w\d-]+\.)*[\w\d-]+\.[\w]{2,}(\/.*)?$/;
    // if (props.form.company_website && !urlPattern.test(props.form.company_website)) return false;

    // Type of Business must be selected
    if (!props.form.business_type) return false;

    // All validations pass
    return true;
});

const goBack = () => {
    emit("goToNextStep", {
        step: "StepOne",
    });
};

const submit = (event) => {
    event.preventDefault();

    const urlPattern = /^(https?:\/\/)?([\w\d-]+\.)*[\w\d-]+\.[\w]{2,}(\/.*)?$/;
    if (props.form.company_website && !urlPattern.test(props.form.company_website)) {
        eventBus.emit("alert:show", {
            title: "Error",
            message:
                "Company website field must by a valid url. Example https://seclock.com",
            level: "danger",
        });
    } else {
        emit("goToNextStep", {
            step: "RegistrationStep2",
        });
    }
};

const goToWebAccessForm = () => {
    emit("goToNextStep", {
        step: "WebAccessForm",
    });
};
</script>
